import type {
    User,
    Department,
    Channel,
    DirectMessage,
    Topic,
    Message,
    Ticket,
    ThreadData,
    CreateTicketData,
    UpdateTicketData,
    CreateDepartmentData,
    UpdateUserData,
    SendMessageData,
    Role,
    GlobalSettings,
    FileRecord,
    Project,
    CreateProjectData,
    ProjectMemberRole,
    ProjectSection,
    ProjectCustomField,
    ProjectCustomFieldType,
    ProjectBulkChanges
} from '../types';

// Environment detection
export const isElectron = navigator.userAgent.includes('Electron');

// Configuration
const ENV_API_URL = (import.meta as any).env?.VITE_API_BASE_URL; // e.g., "https://chat.example.com/api"

// Determine Server Origin (for Socket.io)
export const getServerUrl = () => {
    if (ENV_API_URL) {
        try {
            const url = new URL(ENV_API_URL);
            return url.origin;
        } catch (e) {
            console.error('Invalid VITE_API_BASE_URL:', e);
        }
    }
    return isElectron ? 'http://localhost:3001' : window.location.origin;
};

export const SERVER_URL = getServerUrl();

// Determine API Base URL (for REST calls)
export const API_BASE = ENV_API_URL
    ? (ENV_API_URL.endsWith('/api') ? ENV_API_URL : `${ENV_API_URL.replace(/\/$/, '')}/api`)
    : (isElectron ? 'http://localhost:3001/api' : '/api');
// Helper to ensure URLs are absolute in Electron
export const getAbsoluteUrl = (path: string): string => {
    if (path.startsWith('http')) return path;
    if (isElectron) {
        // Start with SERVER_URL, ensuring no double slashes
        const base = SERVER_URL.endsWith('/') ? SERVER_URL.slice(0, -1) : SERVER_URL;
        const relative = path.startsWith('/') ? path : `/${path}`;
        return `${base}${relative}`;
    }
    return path;
};

const authenticatedFetch = (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
    return fetch(url, { ...options, headers }).then(response => {
        // Global 401 handler - session expired or invalid token
        if (response.status === 401) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            // Note: We don't need to redirect to '/' here. App.tsx and AppContext 
            // listen to state changes or find that currentUser is null and show the login page.
            // In Electron, '/' navigates to the drive root (e.g. C:/) which causes a blank screen.
        }
        return response;
    });
};

// Helper to handle API responses with error checking
const handleResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw {
            status: response.status,
            message: error.error || error.message || `HTTP ${response.status}`,
            response: { data: error }
        };
    }
    return response.json();
};

export const api = {
    // Users
    getUsers: (): Promise<User[]> =>
        authenticatedFetch(`${API_BASE}/users`).then(r => handleResponse<User[]>(r)),

    getCurrentUser: (): Promise<User> =>
        authenticatedFetch(`${API_BASE}/users/me`).then(r => handleResponse<User>(r)),

    updateUser: (userId: string, data: UpdateUserData): Promise<User> =>
        authenticatedFetch(`${API_BASE}/users/${userId}`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        }).then(r => handleResponse<User>(r)),

    // Departments
    getDepartments: (): Promise<Department[]> =>
        authenticatedFetch(`${API_BASE}/departments`).then(r => r.json()),

    createDepartment: (data: CreateDepartmentData): Promise<Department> =>
        authenticatedFetch(`${API_BASE}/departments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => r.json()),

    updateDepartment: (departmentId: string, data: Partial<Department>): Promise<Department> =>
        authenticatedFetch(`${API_BASE}/departments/${departmentId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => r.json()),

    deleteDepartment: (departmentId: string): Promise<{ success: boolean }> =>
        authenticatedFetch(`${API_BASE}/departments/${departmentId}`, {
            method: 'DELETE'
        }).then(r => r.json()),

    // Channels
    getChannels: (): Promise<Channel[]> =>
        authenticatedFetch(`${API_BASE}/channels`).then(r => handleResponse<Channel[]>(r)),

    createChannel: (data: { name: string; is_private?: boolean; allow_member_invites?: boolean; member_ids?: string[]; encryption_mode?: string }): Promise<Channel> =>
        authenticatedFetch(`${API_BASE}/channels`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => handleResponse<Channel>(r)),

    updateChannel: (channelId: string, data: Partial<Channel>): Promise<Channel> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => r.json()),

    getChannelMessages: (channelId: string): Promise<Message[]> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}/messages`).then(r => r.json()),

    inviteToChannel: (channelId: string, userIds: string[]): Promise<Channel> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}/invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_ids: userIds })
        }).then(r => r.json()),

    removeChannelMember: (channelId: string, userId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}/members/${userId}`, {
            method: 'DELETE'
        }).then(r => handleResponse(r)),

    archiveChannel: (channelId: string): Promise<Channel> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}/archive`, {
            method: 'POST'
        }).then(r => r.json()),

    unarchiveChannel: (channelId: string): Promise<Channel> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}/unarchive`, {
            method: 'POST'
        }).then(r => r.json()),

    deleteChannel: (channelId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}`, {
            method: 'DELETE'
        }).then(r => handleResponse(r)),

    // Direct Messages
    getDMs: (): Promise<DirectMessage[]> =>
        authenticatedFetch(`${API_BASE}/dms`).then(r => r.json()),

    createDM: (participantIds: string[]): Promise<DirectMessage> =>
        authenticatedFetch(`${API_BASE}/dms`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participant_ids: participantIds })
        }).then(r => r.json()),

    getDMMessages: (dmId: string): Promise<Message[]> =>
        authenticatedFetch(`${API_BASE}/dms/${dmId}/messages`).then(r => r.json()),

    // Topics
    getTopics: (): Promise<Topic[]> =>
        authenticatedFetch(`${API_BASE}/topics`).then(r => r.json()),

    createTopic: (data: { name: string; description?: string; color?: string; member_ids?: string[]; expires_at?: string; allow_member_invites?: boolean; encryption_mode?: string }): Promise<Topic> =>
        authenticatedFetch(`${API_BASE}/topics`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => r.json()),

    updateTopic: (topicId: string, data: Partial<Topic>): Promise<Topic> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => r.json()),

    inviteToTopic: (topicId: string, userIds: string[]): Promise<Topic> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}/invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_ids: userIds })
        }).then(r => r.json()),

    removeTopicMember: (topicId: string, userId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}/members/${userId}`, {
            method: 'DELETE'
        }).then(r => handleResponse(r)),

    acceptTopicInvite: (topicId: string): Promise<Topic> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}/accept-invite`, {
            method: 'POST'
        }).then(r => r.json()),

    declineTopicInvite: (topicId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}/decline-invite`, {
            method: 'POST'
        }).then(r => r.json()),

    acceptChannelInvite: (channelId: string): Promise<any> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}/accept-invite`, {
            method: 'POST'
        }).then(r => r.json()),

    declineChannelInvite: (channelId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/channels/${channelId}/decline-invite`, {
            method: 'POST'
        }).then(r => r.json()),

    archiveTopic: (topicId: string): Promise<Topic> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}/archive`, {
            method: 'POST'
        }).then(r => r.json()),

    unarchiveTopic: (topicId: string): Promise<Topic> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}/unarchive`, {
            method: 'POST'
        }).then(r => r.json()),

    deleteTopic: (topicId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}`, {
            method: 'DELETE'
        }).then(r => handleResponse(r)),

    getTopicMessages: (topicId: string): Promise<Message[]> =>
        authenticatedFetch(`${API_BASE}/topics/${topicId}/messages`).then(r => r.json()),


    // Messages
    sendMessage: (data: SendMessageData): Promise<Message> =>
        authenticatedFetch(`${API_BASE}/messages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => handleResponse<Message>(r)),

    markMessageAsRead: (messageId: string): Promise<Message> =>
        authenticatedFetch(`${API_BASE}/messages/${messageId}/read`, {
            method: 'POST'
        }).then(r => r.json()),

    toggleReaction: (messageId: string, emoji: string): Promise<Message> =>
        authenticatedFetch(`${API_BASE}/messages/${messageId}/reaction`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emoji })
        }).then(r => r.json()),

    markAsUnread: (messageId: string): Promise<Message> =>
        authenticatedFetch(`${API_BASE}/messages/${messageId}/unread`, {
            method: 'POST'
        }).then(r => r.json()),

    snoozeMessage: (messageId: string, until: string): Promise<Message> =>
        authenticatedFetch(`${API_BASE}/messages/${messageId}/snooze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ until })
        }).then(r => r.json()),

    getUnreadCounts: (): Promise<{ channel: Record<string, number>; dm: Record<string, number>; ticket: Record<string, number>; topic: Record<string, number> }> =>
        authenticatedFetch(`${API_BASE}/unread-counts`).then(r => r.json()),

    getThreadReplies: (messageId: string): Promise<ThreadData> =>
        authenticatedFetch(`${API_BASE}/messages/${messageId}/replies`).then(r => r.json()),

    searchMessages: (query: string, scopeId?: string, scopeType?: string): Promise<any[]> => {
        const params = new URLSearchParams({ q: query });
        if (scopeId) params.append('scopeId', scopeId);
        if (scopeType) params.append('scopeType', scopeType);
        return authenticatedFetch(`${API_BASE}/search?${params.toString()}`).then(r => r.json());
    },

    getEncryptedSearchCache: (): Promise<any[]> =>
        authenticatedFetch(`${API_BASE}/search/encrypted-cache`).then(r => r.json()),

    replyToThread: (messageId: string, data: { content: string, is_broadcast?: boolean, attachments?: any[], is_encrypted?: boolean, encrypted_keys?: any }): Promise<Message> =>
        authenticatedFetch(`${API_BASE}/messages/${messageId}/replies`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => r.json()),

    editMessage: (messageId: string, data: { content: string, is_encrypted?: boolean, self_destruct?: boolean, attachments?: any[], encrypted_keys?: any, author_public_key?: string }): Promise<Message> =>
        authenticatedFetch(`${API_BASE}/messages/${messageId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => handleResponse<Message>(r)),

    deleteMessage: (messageId: string): Promise<Message> =>
        authenticatedFetch(`${API_BASE}/messages/${messageId}`, {
            method: 'DELETE'
        }).then(r => handleResponse<Message>(r)),

    // Tickets
    getTickets: (filters: Record<string, string> = {}): Promise<Ticket[]> => {
        const params = new URLSearchParams(filters).toString();
        return authenticatedFetch(`${API_BASE}/tickets${params ? `?${params}` : ''}`).then(r => handleResponse<Ticket[]>(r));
    },

    getTicket: (ticketId: string): Promise<Ticket> =>
        authenticatedFetch(`${API_BASE}/tickets/${ticketId}`).then(r => handleResponse<Ticket>(r)),

    createTicket: (data: CreateTicketData): Promise<Ticket> =>
        authenticatedFetch(`${API_BASE}/tickets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => handleResponse<Ticket>(r)),

    updateTicket: (ticketId: string, data: UpdateTicketData): Promise<Ticket> =>
        authenticatedFetch(`${API_BASE}/tickets/${ticketId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(r => handleResponse<Ticket>(r)),

    deleteTicket: (ticketId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/tickets/${ticketId}`, {
            method: 'DELETE'
        }).then(r => handleResponse(r)),

    // Projects
    getProjects: (includeArchived = false): Promise<Project[]> =>
        authenticatedFetch(`${API_BASE}/projects${includeArchived ? '?archived=true' : ''}`).then(r => handleResponse<Project[]>(r)),

    createProject: (data: CreateProjectData): Promise<Project> =>
        authenticatedFetch(`${API_BASE}/projects`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
        }).then(r => handleResponse<Project>(r)),

    updateProject: (projectId: string, data: Partial<CreateProjectData> & { is_archived?: boolean }): Promise<Project> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
        }).then(r => handleResponse<Project>(r)),

    archiveProject: (projectId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}`, { method: 'DELETE' }).then(r => handleResponse(r)),

    setProjectMember: (projectId: string, userId: string, role: ProjectMemberRole): Promise<Project> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/members`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: userId, role })
        }).then(r => handleResponse<Project>(r)),

    removeProjectMember: (projectId: string, userId: string): Promise<Project> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/members/${userId}`, { method: 'DELETE' }).then(r => handleResponse<Project>(r)),

    createProjectSection: (projectId: string, data: Pick<ProjectSection, 'name'> & { position?: number }): Promise<ProjectSection> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/sections`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
        }).then(r => handleResponse<ProjectSection>(r)),

    updateProjectSection: (projectId: string, sectionId: string, data: { name?: string; position?: number }): Promise<ProjectSection> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/sections/${sectionId}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
        }).then(r => handleResponse<ProjectSection>(r)),

    deleteProjectSection: (projectId: string, sectionId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/sections/${sectionId}`, { method: 'DELETE' }).then(r => handleResponse(r)),

    createProjectCustomField: (projectId: string, data: {
        name: string; field_type: ProjectCustomFieldType; config?: { options?: string[] };
        position?: number; is_required?: boolean;
    }): Promise<ProjectCustomField> => authenticatedFetch(`${API_BASE}/projects/${projectId}/custom-fields`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
    }).then(r => handleResponse<ProjectCustomField>(r)),

    updateProjectCustomField: (projectId: string, fieldId: string, data: {
        name?: string; config?: { options?: string[] }; position?: number; is_required?: boolean;
    }): Promise<ProjectCustomField> => authenticatedFetch(`${API_BASE}/projects/${projectId}/custom-fields/${fieldId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
    }).then(r => handleResponse<ProjectCustomField>(r)),

    deleteProjectCustomField: (projectId: string, fieldId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/custom-fields/${fieldId}`, { method: 'DELETE' }).then(r => handleResponse(r)),

    addTicketToProject: (projectId: string, ticketId: string, sectionId?: string | null): Promise<Ticket> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/tickets`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ticket_id: ticketId, section_id: sectionId || null })
        }).then(r => handleResponse<Ticket>(r)),

    moveProjectTicket: (projectId: string, ticketId: string, data: { section_id?: string | null; position?: number }): Promise<Ticket> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/tickets/${ticketId}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
        }).then(r => handleResponse<Ticket>(r)),

    removeTicketFromProject: (projectId: string, ticketId: string): Promise<void> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/tickets/${ticketId}`, { method: 'DELETE' }).then(r => handleResponse(r)),

    bulkUpdateProjectTickets: (projectId: string, ticketIds: string[], changes: ProjectBulkChanges): Promise<{ tickets: Ticket[]; updated_count: number }> =>
        authenticatedFetch(`${API_BASE}/projects/${projectId}/tickets/bulk`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ticket_ids: ticketIds, changes })
        }).then(r => handleResponse<{ tickets: Ticket[]; updated_count: number }>(r)),

    setTicketCustomField: (ticketId: string, fieldId: string, value: unknown): Promise<Ticket> =>
        authenticatedFetch(`${API_BASE}/tickets/${ticketId}/custom-fields/${fieldId}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value })
        }).then(r => handleResponse<Ticket>(r)),

    clearTicketCustomField: (ticketId: string, fieldId: string): Promise<Ticket> =>
        authenticatedFetch(`${API_BASE}/tickets/${ticketId}/custom-fields/${fieldId}`, { method: 'DELETE' }).then(r => handleResponse<Ticket>(r)),

    getTicketMessages: (ticketId: string): Promise<Message[]> =>
        authenticatedFetch(`${API_BASE}/tickets/${ticketId}/messages`).then(r => handleResponse<Message[]>(r)),

    // Roles
    getRoles: (): Promise<Role[]> => authenticatedFetch(`${API_BASE}/roles`).then(r => r.json()),
    createRole: (data: Partial<Role>): Promise<Role> => authenticatedFetch(`${API_BASE}/roles`, { method: 'POST', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json' } }).then(r => r.json()),
    updateRole: (roleId: string, data: Partial<Role>): Promise<Role> => authenticatedFetch(`${API_BASE}/roles/${roleId}`, { method: 'PUT', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json' } }).then(r => r.json()),
    deleteRole: (roleId: string): Promise<void> => authenticatedFetch(`${API_BASE}/roles/${roleId}`, { method: 'DELETE' }).then(r => r.json()),

    // Settings
    getSettings: (): Promise<GlobalSettings> => authenticatedFetch(`${API_BASE}/settings`).then(r => r.json()),
    updateSettings: (data: Partial<GlobalSettings>): Promise<GlobalSettings> => authenticatedFetch(`${API_BASE}/settings`, { method: 'PUT', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json' } }).then(r => r.json()),

    // Inbox
    getInbox: (): Promise<Message[]> =>
        authenticatedFetch(`${API_BASE}/inbox`).then(r => r.json()),

    // Storage
    uploadFile: (file: File): Promise<FileRecord> => {
        const formData = new FormData();
        formData.append('file', file);
        const token = localStorage.getItem('auth_token');

        // Don't set Content-Type for FormData - browser will set it with boundary
        return fetch(`${API_BASE}/upload`, {
            method: 'POST',
            headers: {
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: formData
        }).then(r => {
            if (!r.ok) throw new Error('Upload failed');
            return r.json();
        });
    },

    updateFileRetention: (fileId: string, data: { never_delete?: boolean, retention_days?: number }): Promise<FileRecord> =>
        authenticatedFetch(`${API_BASE}/files/${fileId}/retention`, {
            method: 'PUT',
            body: JSON.stringify(data)
        }).then(r => handleResponse<FileRecord>(r)),

    // Auth
    login: (email: string, password: string): Promise<{ success: boolean; token?: string; user?: { id: string; name: string; email: string; role_id?: string; public_key?: string; enc_private_key?: string; key_salt?: string }; requires_2fa?: boolean; user_id?: string }> =>
        fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw { response: { data } };
            if (data.token) localStorage.setItem('auth_token', data.token);
            return data;
        }),

    verify2FA: (user_id: string, code: string): Promise<{ success: boolean; token?: string; user: { id: string; name: string; email: string; role_id?: string } }> =>
        fetch(`${API_BASE}/auth/verify-2fa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id, code })
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw { response: { data } };
            if (data.token) localStorage.setItem('auth_token', data.token);
            return data;
        }),

    setupPassword: (email: string, invite_token: string | undefined, password: string, keys?: { public_key: string; enc_private_key: string; key_salt: string }): Promise<{ success: boolean; token?: string; user: { id: string; name: string; email: string; role_id?: string } }> =>
        fetch(`${API_BASE}/auth/setup-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, invite_token, password, ...keys })
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw { response: { data } };
            if (data.token) localStorage.setItem('auth_token', data.token);
            return data;
        }),

    forgotPassword: (email: string): Promise<{ success: boolean; message: string }> =>
        fetch(`${API_BASE}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw { response: { data } };
            return data;
        }),

    resetPassword: (token: string, password: string): Promise<{ success: boolean; message: string }> =>
        fetch(`${API_BASE}/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password })
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw { response: { data } };
            return data;
        }),

    logout: (): Promise<{ success: boolean }> =>
        authenticatedFetch(`${API_BASE}/auth/logout`, { method: 'POST' }).then(r => {
            localStorage.removeItem('auth_token');
            return r.json();
        }),

    // Admin user actions
    inviteUser: (data: { name: string; email: string; role_id?: string; department_id?: string }): Promise<{ success: boolean; user: User; invite_token: string; invite_link: string }> =>
        authenticatedFetch(`${API_BASE}/admin/users/invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(async r => {
            const responseData = await r.json();
            if (!r.ok) throw { response: { data: responseData } };
            return responseData;
        }),

    adminResetPassword: (userId: string): Promise<{ success: boolean; reset_token: string }> =>
        authenticatedFetch(`${API_BASE}/admin/users/${userId}/reset-password`, {
            method: 'POST'
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw { response: { data } };
            return data;
        }),

    deleteUser: (userId: string): Promise<{ success: boolean }> =>
        authenticatedFetch(`${API_BASE}/admin/users/${userId}`, {
            method: 'DELETE'
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw { response: { data } };
            return data;
        }),

    reinviteUser: (userId: string): Promise<{ success: boolean; invite_link?: string }> =>
        authenticatedFetch(`${API_BASE}/admin/users/${userId}/reinvite`, {
            method: 'POST'
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw { response: { data } };
            return data;
        }),

    // E2EE Key Management
    saveKeys: (data: { enc_private_key: string; key_salt: string; public_key: string; enc_private_key_escrow?: string; recovery_key?: string }): Promise<{ success: boolean }> =>
        authenticatedFetch(`${API_BASE}/auth/save-keys`, {
            method: 'POST',
            body: JSON.stringify(data)
        }).then(async r => {
            const responseData = await r.json();
            if (!r.ok) throw { response: { data: responseData } };
            return responseData;
        }),

    /**
     * Upload the OAuth-derived encrypted private key blob.
     * The id_token is sent for server-side verification (Google tokeninfo / MSAL JWKS).
     */
    savePasskeyBlob: (data: {
        enc_private_key_passkey: string;
        key_salt_passkey: string;
        oauth_provider: 'google' | 'microsoft';
        id_token: string;
    }): Promise<{ success: boolean }> =>
        authenticatedFetch(`${API_BASE}/auth/save-passkey-blob`, {
            method: 'POST',
            body: JSON.stringify(data)
        }).then(async r => {
            const responseData = await r.json();
            if (!r.ok) throw { response: { data: responseData } };
            return responseData;
        }),

    /**
     * Fetch which OAuth providers are configured by the admin.
     * Used to show/hide provider buttons without exposing credentials in the frontend bundle.
     */
    getOAuthConfig: (): Promise<{
        google_client_id?: string;
        microsoft_client_id?: string;
        microsoft_tenant_id?: string;
    }> =>
        fetch(`${API_BASE}/settings/oauth-config`).then(r => r.json()),

    /**
     * Exchange an OAuth ID token for the user's encrypted passkey blob.
     * Server verifies the token, matches the email, returns the encrypted blob.
     * The private key is only decrypted client-side.
     */
    oauthRecovery: (data: {
        provider: 'google' | 'microsoft';
        id_token: string;
    }): Promise<{
        enc_private_key_passkey: string;
        key_salt_passkey: string;
        public_key: string;
    }> =>
        fetch(`${API_BASE}/auth/oauth-recovery`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(async r => {
            const responseData = await r.json();
            if (!r.ok) throw { response: { data: responseData } };
            return responseData;
        }),
};
